import { Component, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { CustomerSearchResult } from '../../../../../../../../../models/customer';
import { AccountService } from '../../../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../../../services/customer/customer.service';
import { ListResponse, ListResponseCountOptional } from '../../../../../../../../../models/list-response';
import { dateStringFromISO8601, getISO8601DateStringFromMoment, propExists } from '../../../../../../../../../utils';
import { CustomerEntryPass} from '../../../../../../../../../models/entry-pass';
import { CustomerEntryPassStatus } from '../../../../../../../../../models/entry-pass-enums';
import * as moment from 'moment';
import {
  OrganizationConfigService
} from '../../../../../../../../../services/organization-config/organization-config.service';

@Component({
  selector: 'app-choose-check-in-shared-pass-modal',
  templateUrl: './choose-check-in-shared-pass-modal.component.html',
  styleUrls: ['./choose-check-in-shared-pass-modal.component.css']
})
export class ChooseCheckInSharedPassModalComponent implements OnInit {

  @Input() passChosenCallback: (passId: number) => undefined;

  public loadingCustomers = false;
  public search = '';
  public customerFiltersChangedSubject = new Subject<null>();
  public customerList: ListResponseCountOptional<CustomerSearchResult>;

  public loadingPasses = false;
  public passList: ListResponse<CustomerEntryPass>;

  public customerSelected = false;

  public serverError: number = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public dateStringFromISO8601 = dateStringFromISO8601;

  // The number of customer list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private customerListRequestCount = 0;

  private recentActivityFilter: string | undefined = undefined;

  constructor(
    public accountService: AccountService,
    public activeModal: NgbActiveModal,
    public orgConfigService: OrganizationConfigService,
    private customerService: CustomerService
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    // Handle changes to the customer filters by reloading the customers,
    // subject to debounce.
    this.customerFiltersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadCustomers();
    });
  }

  /**
   * Handle a change in the customer filters. The list of customers will be
   * filtered according to the new values.
   */
  public handleCustomerFilterChange() {
    this.loadingCustomers = true;

    this.customerSelected = false;

    const orgConfig = this.orgConfigService.getOrganizationConfig();
    const mustApplyRecentActivityFilter = (
      orgConfig &&
      orgConfig.customer_search_recent_activity_window_months
    );
    if (mustApplyRecentActivityFilter) {
      this.recentActivityFilter = getISO8601DateStringFromMoment(
        moment().subtract(orgConfig.customer_search_recent_activity_window_months, 'months')
      );
    }

    this.customerFiltersChangedSubject.next();
  }

  /**
   * Load the page of customers corresponding to the current page and filter
   * selections.
   */
  public loadCustomers() {
    this.loadingCustomers = true;

    // Increment the request count and save a local copy.
    this.customerListRequestCount++;
    const thisRequestCount = this.customerListRequestCount;

    if (!this.hasSearchInput()) {
      this.customerList = null;
      this.loadingCustomers = false;
      return;
    }

    this.customerService
      .searchCustomers(
        1,
        200,
        {
          search: this.search,
          last_activity_timestamp_min: this.recentActivityFilter,
        },
        true
      )
      .then(response => {
        // Only update the customer list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.customerListRequestCount) {
          this.customerList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.customerListRequestCount) {
          this.loadingCustomers = false;
        }
      });
  }

  /**
   * Whether a non-zero number of customer results have been loaded.
   */
  public customerResultsPresent(): boolean {
    return (
      this.customerList &&
      this.customerList.results &&
      (this.customerList.results.length > 0)
    );
  }

  public filteredCustomerResults(): CustomerSearchResult[] {
    return this.customerList.results;
  }

  /**
   * Handle a click on one of the customer results by looking up its entry
   * passes and displaying the customer's name in the search input.
   */
  public selectRelatedCustomer(customer: CustomerSearchResult) {
    this.customerSelected = true;
    this.search = (
      customer.first_name + ' ' + customer.last_name
    );

    this.loadingPasses = true;

    this.customerService
      .getCustomerEntryPasses(customer.id, 1, 100)
      .then(response => {
        this.passList = response;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingPasses = false; });
  }

  /**
   * Whether a non-zero number of pass results have been loaded.
   */
  public passResultsPresent(): boolean {
    return (
      this.passList &&
      this.passList.results &&
      (this.filteredPassResults().length > 0)
    );
  }

  public filteredPassResults(): CustomerEntryPass[] {
    return this.passList.results.filter(pass => {
      return (pass.status === CustomerEntryPassStatus.active);
    });
  }

  public hasSearchInput() {
    return this.search.trim().length >= 3;
  }

  public hasRecentActivityFilter(): boolean {
    return propExists(this.recentActivityFilter);
  }

  public handleExpandSearch() {
    this.recentActivityFilter = undefined;
    this.loadingCustomers = true;
    this.customerFiltersChangedSubject.next();
  }

}
